import { createColumnHelper, DisplayColumnDef } from '@tanstack/react-table';
import { TAggregatedReportData } from '../../../../../types';
import { CompanyCell } from '../../../../../components/Table/CellRenderers/CompanyCell';
import { styled, useTheme } from '@mui/material';
import { Typography } from '../../../../../components/Typography/Typography';
import { useBreakdownData } from '../../../hooks/useBreakdownData';
import { Tooltip } from '../../../../../components/Tooltip/Tooltip';
import { AnswerCell } from '../../AnswerCell/AnswerCell';

const columnHelper = createColumnHelper<TAggregatedReportData>();

const ColName = styled('div')`
  p {
    width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const useBreakdownTableColumns = () => {
  const { colors } = useTheme();
  const { breakdownTableData } = useBreakdownData();

  const columnsToShow = breakdownTableData?.[0]?.breakdownReportData?.map((data) => {
    return {
      id: data.questionId,
      name: data.questionName,
      sourceType: data?.sourceType,
    };
  });

  const nameCol = columnHelper.accessor('companyName', {
    cell: (info) => (
      <CompanyCell
        name={info.getValue()}
        logo={info.row.original.logoUrl}
        labelColor={colors.accent[50]}
      />
    ),
    header: () => (
      <Typography variant='overline' color={colors.primary[70]}>
        Issuer
      </Typography>
    ),
    meta: {
      width: '240px',
      minWidth: '240px',
      tdStyles: {
        position: 'sticky',
        left: 0,
        zIndex: 1,
        paddingLeft: 16,
        height: '100%',
        backgroundColor: '#fff',
        borderRight: `1px solid ${colors.primary[30]}`,
      },
      thStyles: {
        position: 'sticky',
        left: 0,
        zIndex: 1,
        borderRight: `1px solid ${colors.primary[30]}`,
      },
    },
    enableColumnFilter: true,
  });

  const dynamicColumns: DisplayColumnDef<TAggregatedReportData>[] =
    columnsToShow
      ?.map?.((column) => {
        return columnHelper.display({
          id: column?.id ? String(column?.id) : '',
          cell: (info) => {
            const data = info.row.original.breakdownReportData?.find(
              (questionData) => questionData.questionId === column?.id
            );
            const answer = data?.answer || '';
            const questionId = data?.questionId || '';
            return answer ? (
              <AnswerCell questionId={questionId} sourceType={column.sourceType} answer={answer} />
            ) : null;
          },
          header: () => (
            <ColName>
              <Tooltip
                PopperProps={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [55, -13],
                      },
                    },
                  ],
                }}
                title={column?.name}
              >
                <Typography variant='overline' color={colors.primary[70]}>
                  {column?.name}
                </Typography>
              </Tooltip>
            </ColName>
          ),
          meta: {
            width: '200px',
            minWidth: '200px',
            tdStyles: {
              paddingLeft: 16,
            },
            thStyles: {
              paddingLeft: 16,
            },
          },
          enableSorting: false,
        });
      })
      ?.filter((col) => Boolean(col)) || [];

  return [nameCol, ...dynamicColumns];
};
